import { useQuery } from "@tanstack/react-query";
import { parseUpgradeRequiredKey } from "@/lib/queryClient";
import { ModuleUpgradePanel, parseModuleUpgradeKey } from "@/components/module-upgrade-panel";
import { UpgradeContent } from "@/components/upgrade-modal";
import { resolveUpgradeInfo } from "@/lib/upgrade-info";
import { cn } from "@/lib/utils";
import { useIsV3Billing } from "@/hooks/use-v3-billing";
import { type AccountType } from "@shared/schema";

/**
 * In-page "Upgrade to unlock" panel (Task #4441). When a tier-locked feature
 * page's main query 403s with `UPGRADE_REQUIRED`, the page renders this instead
 * of its generic error/empty state so the user sees a clean upgrade CTA rather
 * than a broken UI behind the global upgrade modal.
 *
 * It resolves the locked section's label + required tier from the error `key`
 * plus the shared `/api/app/menu-tier-locks/denied` source (same as the global
 * `UpgradeRequiredListener`), so the inline panel and the modal name the same
 * section and tier.
 */
export function InlineUpgradePanel({
  error,
  className,
}: {
  error: unknown;
  className?: string;
}) {
  const key = parseUpgradeRequiredKey(error);
  // Modular entitlement gates carry a module key (e.g. "cleaning") instead of
  // a menu-lock id — those render the module upgrade prompt (price + deep
  // link into the plan editor), so every page using this panel gets the
  // modular UX for free.
  const moduleKey = parseModuleUpgradeKey(error);
  const tierLocksQuery = useQuery<{
    deniedKeys: string[];
    lockedTiers?: Record<string, AccountType>;
  }>({
    queryKey: ["/api/app/menu-tier-locks/denied"],
    staleTime: 60_000,
  });
  const { label, requiredTier, reason } = resolveUpgradeInfo(
    key,
    tierLocksQuery.data?.lockedTiers,
  );
  // v3 per-property accounts have every feature — no upgrade pitch (B13).
  const isV3 = useIsV3Billing();
  if (isV3) return null;

  if (moduleKey) {
    return <ModuleUpgradePanel moduleKey={moduleKey} className={className} />;
  }

  return (
    <div
      className={cn("flex items-start justify-center p-4 sm:p-8", className)}
      data-testid="panel-inline-upgrade"
    >
      <div className="mt-6 w-full sm:max-w-md rounded-lg border bg-background p-6 shadow-sm">
        <UpgradeContent feature={label} requiredTier={requiredTier} reason={reason} />
      </div>
    </div>
  );
}
